import { Injectable, computed, signal } from '@angular/core';
import { Table } from 'primeng/table';
import { DEFAULT_PAGE_SIZE } from '../table/table.component';

@Injectable()
export class PaginatorService<TData> {
  private readonly _value = signal<TData[]>([]);
  private readonly _first = signal(0);
  private readonly _rows = signal(DEFAULT_PAGE_SIZE);

  public readonly first = this._first.asReadonly();
  public readonly rows = this._rows.asReadonly();

  public readonly data = computed(() => {
    return this.table?.filteredValue ?? this._value();
  });

  public readonly totalRecords = computed(() => this.data().length);

  public readonly currentPage = computed(() =>
    Math.floor(this.first() / this.rows()),
  );

  public readonly totalPages = computed(() =>
    Math.ceil(this.totalRecords() / this.rows()),
  );

  public readonly pages = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i),
  );

  private table!: Table;

  public init(value: TData[], table: Table): void {
    this._value.set(value);
    this.table = table;

    this._first.set(table.first ?? 0);
    this._rows.set(table.rows ?? DEFAULT_PAGE_SIZE);
  }

  public setValue(value: TData[]): void {
    this._value.set(value);
  }

  public goToPage(page: number): void {
    const first = page * this.rows();

    this._first.set(first);
    this.table.first = first;
  }

  public nextPage(): void {
    this.goToPage(Math.min(this.currentPage() + 1, this.totalPages() - 1));
  }

  public previousPage(): void {
    this.goToPage(Math.max(this.currentPage() - 1, 0));
  }
}
